<template>
  <v-app class="rounded rounded-md">
    <Sidebar v-if="showSidebar" />

    <!-- Session expired dialog -->
    <v-dialog v-model="sessionExpiredDialog" max-width="400" persistent>
      <v-card>
        <v-card-title class="text-h6">Session Expired</v-card-title>
        <v-card-text>Your session has expired. Please log in again to continue.</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="primary" variant="flat" @click="goToLogin">Go to Login</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Not-authorized (403) snackbar — no logout -->
    <v-snackbar v-model="forbiddenSnackbar" color="error" :timeout="5000" location="top">
      {{ forbiddenMessage }}
      <template #actions>
        <v-btn variant="text" @click="forbiddenSnackbar = false">{{ $t('generic.close') }}</v-btn>
      </template>
    </v-snackbar>

    <v-main style="min-height: 300px;">
      <!-- Keyed on path, not fullPath: query-only changes (?domain=, ?view=) are
           handled inside the page and must not remount it (double load + blink). -->
      <router-view v-slot="{ Component }">
        <transition :name="transitionName">
          <component :is="Component" :key="$route.path + ':' + domainRevision" />
        </transition>
      </router-view>
    </v-main>
  </v-app>
</template>

<script>
import Sidebar from './components/Sidebar.vue';
import { refresh_current_user } from './api';

export default {
  components: {
    Sidebar,
  },

  data() {
    return {
      transitionName: 'fade',
      sessionExpiredDialog: false,
      forbiddenSnackbar: false,
      forbiddenMessage: '',
      domainRevision: 0,
    };
  },

  computed: {
    showSidebar() {
      return this.$route.meta.hideSidebar !== true;
    },
  },

  mounted() {
    window.addEventListener('auth:expired', this.handleAuthExpired);
    window.addEventListener('auth:forbidden', this.handleAuthForbidden);
    window.addEventListener('domains:updated', this.handleDomainsUpdated);
    this.syncDomainsFromServer();
  },

  beforeUnmount() {
    window.removeEventListener('auth:expired', this.handleAuthExpired);
    window.removeEventListener('auth:forbidden', this.handleAuthForbidden);
    window.removeEventListener('domains:updated', this.handleDomainsUpdated);
  },

  watch: {
    $route(to, from) {
      const toDepth = to.meta.depth ?? 0;
      const fromDepth = from.meta.depth ?? 0;

      if (toDepth > fromDepth) {
        this.transitionName = 'slide-left';
      } else if (toDepth < fromDepth) {
        this.transitionName = 'slide-right';
      } else {
        this.transitionName = 'fade';
      }
    },
  },

  methods: {
    handleAuthExpired() {
      this.sessionExpiredDialog = true;
    },
    handleAuthForbidden(event) {
      this.forbiddenMessage = event?.detail || this.$t('generic.notAuthorized');
      this.forbiddenSnackbar = true;
    },
    goToLogin() {
      this.sessionExpiredDialog = false;
      this.$router.push({ name: 'login' });
    },
    handleDomainsUpdated() {
      this.domainRevision += 1;
    },
    async syncDomainsFromServer() {
      if (!localStorage.getItem('authToken')) {
        return;
      }
      try {
        await refresh_current_user();
      } catch {
        // 401 is handled by the axios interceptor.
      }
    },
  },
};
</script>

<style>
/* Slide left (going deeper) */
.slide-left-enter-active,
.slide-left-leave-active,
.slide-right-enter-active,
.slide-right-leave-active {
  transition: transform 0.3s ease, opacity 0.05s ease;
  position: absolute;
  width: 100%;
}

.slide-left-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-left-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Slide right (going back) */
.slide-right-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-right-leave-to {
  transform: translateX(100%);
  opacity: 0;
}

/* Fade fallback */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.1s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Ensure the v-main has relative positioning for absolute children */
.v-main__wrap {
  position: relative;
  overflow: hidden;
}
</style>
